import styled from "styled-components";
import Modal from "../modal";
import { Button } from "melodies-source/Button";
import { useState } from "react";
import { useDesktopMediaQuery } from "hooks";
import { CircularLoader } from "Components";
import { TextInput } from "melodies-source/TextInput";
import CheckCircleIcon from "../CheckCircleIcon";
import { getFunctions, httpsCallable } from "firebase/functions";

type FlowStatus = "enterPassword" | "connecting" | "connected";

type SonyConnectModalProps = {
  artistGroupId: string;
  isOpen: boolean;
  onClose: () => void;
};

export const SonyConnectModal = ({
  artistGroupId,
  isOpen,
  onClose,
}: SonyConnectModalProps) => {
  const [status, setStatus] = useState<FlowStatus>("enterPassword");
  const [connectError, setConnectError] = useState("");
  const [password, setPassword] = useState("");

  const isDesktop = useDesktopMediaQuery();

  const onSubmitPassword = async () => {
    setConnectError("");
    setStatus("connecting");

    try {
      await httpsCallable(
        getFunctions(),
        "services-sonyActions",
      )({
        action: "connect",
        artistGroupId,
        password,
      });

      setStatus("connected");
    } catch (error) {
      console.error(error);

      // The callable throws "permission-denied" ("Incorrect password") for a bad
      // password; anything else is unexpected, so show a generic message.
      const code = (error as { code?: string })?.code;
      setConnectError(
        code === "functions/permission-denied"
          ? "Incorrect password, please try again."
          : "Something went wrong, please try again.",
      );

      setStatus("enterPassword");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} service="sony">
      {status === "enterPassword" && (
        <>
          <HeaderText isDesktop={isDesktop}>Connect to Sony Music</HeaderText>
          <br />
          <BodyText isDesktop={isDesktop}>
            Enter the Sony Music connection password to enable the integration
            for your artist:
          </BodyText>
          <br />
          <TextInput
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(text) => setPassword(text)}
            hasError={!!connectError}
          />
          {!!connectError && (
            <ErrorText isDesktop={isDesktop}>{connectError}</ErrorText>
          )}
          <br />
          <StyledButton disabled={!password} onClick={onSubmitPassword}>
            Connect
          </StyledButton>
        </>
      )}
      {status === "connecting" && (
        <>
          <CircularLoader
            height={isDesktop ? 60 : 41}
            sx={{ marginBottom: 10 }}
          />
          <BodyText isDesktop={isDesktop}>Please wait...</BodyText>
          <BodyText isDesktop={isDesktop}>
            We're verifying the password and connecting to Sony Music
          </BodyText>
        </>
      )}
      {status === "connected" && (
        <>
          <CheckCircleIcon />
          <BodyText isDesktop={isDesktop}>Success!</BodyText>
          <BodyText isDesktop={isDesktop}>
            You're connected to Sony Music
          </BodyText>
          <Button style={{ marginTop: 20 }} onClick={onClose}>
            Done
          </Button>
        </>
      )}
    </Modal>
  );
};

export const SonyDisconnectModal = ({
  artistGroupId,
  onClose,
  isOpen,
}: {
  artistGroupId: string;
  onClose: () => void;
  isOpen: boolean;
}) => {
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState("");

  const isDesktop = useDesktopMediaQuery();

  const onDisconnect = async () => {
    setDisconnecting(true);
    setDisconnectError("");

    try {
      await httpsCallable(
        getFunctions(),
        "services-sonyActions",
      )({ action: "disconnect", artistGroupId });

      onClose();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      console.error(errorMessage);

      setDisconnectError(`Error disconnecting Sony Music: ${errorMessage}`);
      setDisconnecting(false);
    }
  };

  const modalBody = () => {
    if (disconnectError) {
      return (
        <HeaderText style={{ color: "#e45c52" }} isDesktop={isDesktop}>
          {disconnectError}
        </HeaderText>
      );
    }

    if (disconnecting) {
      return (
        <>
          <CircularLoader height={30} sx={{ marginRight: "0.5rem" }} />
          <br />
          <BodyText isDesktop={isDesktop}>
            Disconnecting Sony Music...
          </BodyText>
        </>
      );
    }
    return (
      <HeaderText isDesktop={isDesktop}>
        Are you sure you want to disconnect Sony Music?
      </HeaderText>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} service="sony">
      {modalBody()}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          marginTop: 40,
        }}
      >
        <Button
          onClick={() => onDisconnect()}
          style={{ marginRight: "2rem" }}
          disabled={disconnecting}
        >
          Confirm
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={disconnecting}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

const BodyText = styled.div<{ isDesktop: boolean }>`
  font-weight: 400;
  font-size: ${({ isDesktop }) => (isDesktop ? "17px" : "12px")};
  line-height: ${({ isDesktop }) => (isDesktop ? "25.5px" : "18px")};
`;

const ErrorText = styled.div<{ isDesktop: boolean }>`
  color: #e45c52;
  font-weight: 500;
  font-size: ${({ isDesktop }) => (isDesktop ? "min(1.3vw, 12px)" : "13px")};
  line-height: 19.5px;
  margin-top: 5px;
  align-self: flex-start;
  margin-left: 10px;
`;

const HeaderText = styled.div<{ isDesktop: boolean }>`
  font-weight: 600;
  font-size: ${({ isDesktop }) => (isDesktop ? "20px" : "17px")};
  line-height: ${({ isDesktop }) => (isDesktop ? "30px" : "25.5px")};
`;

const StyledButton = styled(Button)`
  max-width: 385px;
`;
